import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { financeService } from "../../services/financeService";
import Icon from "../../components/Common/Icon";
import AdvancedTable from "../../components/Common/AdvancedTable";
import { useToast } from "../../context/ToastContext";
import { useChartColors } from "../../theme/ThemeContext";

const fmtTL = (n) =>
  (n == null ? 0 : Number(n)).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("tr-TR", { month: "short", year: "2-digit" });
};

// Ayın ilk ve son günü (varsayılan: bu ay)
const monthBounds = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
};

const ACCENTS = {
  success: "bg-success/10 text-success",
  error: "bg-error/10 text-error",
  primary: "bg-primary/10 text-primary",
};

function StatCard({ label, value, icon, accent = "primary", valueClass }) {
  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${ACCENTS[accent]}`}>
        <Icon name={icon} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-on-surface-variant truncate">{label}</p>
        <p className={`text-xl font-bold ${valueClass ?? "text-on-surface"}`}>{fmtTL(value)}</p>
      </div>
    </div>
  );
}

export default function FinancePage() {
  const { showToast } = useToast();
  const C = useChartColors();
  const [range, setRange] = useState(monthBounds());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    financeService
      .summary(range.from, range.to)
      .then((res) => setData(res.data?.data ?? null))
      .catch(() => showToast("Finans raporu yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  }, [range.from, range.to]); // eslint-disable-line react-hooks/exhaustive-deps

  const trendData = useMemo(
    () =>
      (data?.monthlyTrend ?? []).map((p) => ({
        name: monthLabel(p.month),
        Gelir: Number(p.income || 0),
        Gider: Number(p.expense || 0),
      })),
    [data]
  );

  const net = Number(data?.net || 0);

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="insights" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Finans Yönetimi</h1>
            <p className="text-on-surface-variant text-sm">Dönemsel gelir, gider ve kâr-zarar analizi.</p>
          </div>
        </div>

        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Başlangıç</label>
            <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
              className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Bitiş</label>
            <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary" />
          </div>
        </div>
      </div>

      {/* Özet kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Toplam Gelir" value={data?.totalIncome} icon="trending_up" accent="success" valueClass="text-success" />
        <StatCard label="Toplam Gider" value={data?.totalExpense} icon="trending_down" accent="error" valueClass="text-error" />
        <StatCard
          label="Kâr / Zarar"
          value={net}
          icon={net >= 0 ? "savings" : "warning"}
          accent={net >= 0 ? "primary" : "error"}
          valueClass={net >= 0 ? "text-primary" : "text-error"}
        />
      </div>

      {loading ? (
        <div className="bg-surface-card rounded-2xl p-10 border border-outline-variant text-center text-on-surface-variant">Yükleniyor…</div>
      ) : (
        <>
          {/* Aylık trend */}
          <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
            <h3 className="text-sm font-bold text-on-surface mb-1">Aylık Gelir / Gider</h3>
            <p className="text-xs text-on-surface-variant mb-3">Seçili dönemin aya göre dağılımı</p>
            {trendData.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-on-surface-variant">Veri yok</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={trendData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} width={60}
                         tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                  <Tooltip formatter={(v) => fmtTL(v)} cursor={{ fill: "rgba(124,58,237,0.06)" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Gelir" fill={C.success} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Gider" fill={C.error} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Kategori kırılımları */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <CategoryCard title="Gelir Kategorileri" rows={data?.incomeByCategory ?? []} />
            <CategoryCard title="Gider Kategorileri" rows={data?.expenseByCategory ?? []} />
          </div>

          {/* Aylık kasa raporu — günlük hareket tablosu */}
          <MonthlyCashReport initialMonth={range.from.slice(0, 7)} showToast={showToast} />
        </>
      )}
    </div>
  );
}

/* --------------------------------------------------- Aylık Kasa Raporu */

const DAY_STATUS = {
  // Hareketi olup kasası açılmamış gün mutabakat görmemiştir; ayın kesinleşmesini
  // engellediği için nötr değil uyarı tonunda gösterilir.
  NONE: { label: "Kasa Açılmadı", cls: "bg-tier-gold/15 text-tier-gold" },
  OPEN: { label: "Açık", cls: "bg-success/15 text-success" },
  CLOSED: { label: "Kesinleşti", cls: "bg-primary/10 text-primary" },
};

function MonthlyCashReport({ initialMonth, showToast }) {
  const [month, setMonth] = useState(initialMonth);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    financeService
      .monthly(month)
      .then((res) => setReport(res.data?.data ?? null))
      .catch(() => showToast("Aylık kasa raporu yüklenemedi.", "error"))
      .finally(() => setLoading(false));
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      key: "date",
      header: "Gün",
      type: "string",
      sortable: true,
      render: (v) => new Date(v).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", weekday: "short" }),
    },
    {
      key: "status",
      header: "Durum",
      type: "string",
      sortable: true,
      render: (v) => (
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${(DAY_STATUS[v] || DAY_STATUS.NONE).cls}`}>
          {(DAY_STATUS[v] || DAY_STATUS.NONE).label}
        </span>
      ),
    },
    {
      key: "openingBalance",
      header: "Açılış",
      type: "number",
      sortable: true,
      // Otomatik açılan günde bakiye sayılmadı, devredildi → "≈" ile ayırt edilir.
      render: (v, row) =>
        v == null ? (
          "—"
        ) : row.autoOpened ? (
          <span title="Gün otomatik açıldı; açılış bakiyesi sayılmadı, önceki kapanıştan devredildi.">
            ≈ {fmtTL(v)}
          </span>
        ) : (
          fmtTL(v)
        ),
    },
    { key: "income", header: "Gelir", type: "number", sortable: true, render: (v) => <span className="text-success font-semibold">{fmtTL(v)}</span> },
    { key: "expense", header: "Gider", type: "number", sortable: true, render: (v) => <span className="text-error font-semibold">{fmtTL(v)}</span> },
    {
      key: "net",
      header: "Net",
      type: "number",
      sortable: true,
      render: (v) => <span className={`font-bold ${Number(v) >= 0 ? "text-on-surface" : "text-error"}`}>{fmtTL(v)}</span>,
    },
    { key: "closingBalance", header: "Kapanış", type: "number", sortable: true, render: (v) => (v == null ? "—" : fmtTL(v)) },
    { key: "transactionCount", header: "Hareket", type: "number", sortable: true, render: (v) => `${v} adet` },
  ];

  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-on-surface flex items-center gap-2">
            <Icon name="calendar_month" className="!text-[18px] text-primary" /> Aylık Kasa Raporu
            {report && (
              <span
                className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex items-center gap-1 ${
                  report.finalized ? "bg-success/15 text-success" : "bg-tier-gold/15 text-tier-gold"
                }`}
              >
                <Icon name={report.finalized ? "verified" : "hourglass_top"} className="!text-[14px]" />
                {report.finalized ? "Resmî (Kesinleşti)" : "Önizleme"}
              </span>
            )}
          </h3>
          <p className="text-xs text-on-surface-variant mt-0.5">
            {report && !report.finalized
              ? `Ayın kesinleşmesi için ${[
                  report.openDays > 0 ? `${report.openDays} açık gün kapatılmalı` : null,
                  report.missingDays > 0 ? `${report.missingDays} gün için kasa açılıp kapatılmalı` : null,
                ]
                  .filter(Boolean)
                  .join(", ") || "en az bir gün kesinleştirilmeli"}.`
              : "Ayın her günü için kasa hareket özeti."}
          </p>
        </div>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="border border-outline-variant rounded-xl px-3 py-2 text-sm bg-white outline-none focus:border-primary"
        />
      </div>

      {/* Ay toplam kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Ay Toplam Gelir" value={report?.totalIncome} icon="trending_up" accent="success" valueClass="text-success" />
        <StatCard label="Ay Toplam Gider" value={report?.totalExpense} icon="trending_down" accent="error" valueClass="text-error" />
        <StatCard label="Ay Net" value={report?.net} icon="account_balance_wallet" accent="primary" />
      </div>

      {/* Kasası hiç açılmamış hareketli günler: mutabakat görmemiş, ayın
          kesinleşmesini engelliyor. Hangi günler olduğu tek tek gösterilir. */}
      {report?.missingDays > 0 && (
        <div className="flex items-start gap-2 text-sm text-tier-gold bg-tier-gold/10 border border-tier-gold/25 rounded-xl px-4 py-3">
          <Icon name="warning" className="!text-[18px] mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">
              {report.missingDays} günde kasa açılmadan işlem yapılmış.
            </p>
            <p className="text-xs mt-0.5 text-on-surface-variant">
              Bu günlerin hareketleri toplamlara dahildir, ancak gün mutabakatı yapılmadığı için ay
              kesinleşmiş sayılmaz:{" "}
              <span className="font-medium text-on-surface">
                {report.days
                  .filter((d) => d.status === "NONE")
                  .map((d) => new Date(d.date).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }))
                  .join(", ")}
              </span>
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-on-surface-variant text-sm">Yükleniyor…</div>
      ) : (report?.days ?? []).length === 0 ? (
        <div className="p-8 text-center text-on-surface-variant text-sm">Bu ay için kasa hareketi yok.</div>
      ) : (
        <AdvancedTable columns={columns} data={report.days} searchPlaceholder="Günlerde ara..." />
      )}
    </div>
  );
}

function CategoryCard({ title, rows }) {
  const PIE = useChartColors().pie;
  const pieData = rows.map((r) => ({ name: r.category, value: Number(r.amount || 0) }));
  return (
    <div className="bg-surface-card rounded-2xl border border-outline-variant p-4">
      <h3 className="text-sm font-bold text-on-surface mb-3">{title}</h3>
      {pieData.length === 0 ? (
        <div className="h-[220px] flex items-center justify-center text-sm text-on-surface-variant">Veri yok</div>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <ResponsiveContainer width="100%" height={200} className="max-w-[220px]">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {pieData.map((d, i) => (
                  <Cell key={d.name} fill={PIE[i % PIE.length]} stroke="none" />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmtTL(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex-1 w-full space-y-1.5">
            {pieData.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-on-surface-variant truncate">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: PIE[i % PIE.length] }} />
                  {d.name}
                </span>
                <span className="font-semibold text-on-surface">{fmtTL(d.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
